import React, { useMemo } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'

import { Input, OnePagination } from '@factorialco/f0-react/dist/experimental'
import { Search } from '@factorialco/f0-react/icons/app'
import { useQuery } from '@apollo/client'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import { ResponsivePage } from 'modules/layout/components/ResponsivePage'

import { useItManagementFeatureAvailability } from 'modules/it_management/hooks/useItManagementFeatureAvailability'
import { useDeviceCatalogFilters } from 'modules/it_management/hooks/device_catalog/useDeviceCatalogFilters'
import { useDeviceCatalogSavedDevices } from 'modules/it_management/hooks/device_catalog/useDeviceCatalogSavedDevices'
import { useSidebarSections } from 'modules/it_management/hooks/device_catalog/useSidebarSections'
import { mapVariantToCardProps } from 'modules/it_management/hooks/device_catalog/mapVariantToCardProps'
import {
  CATEGORY_TYPE_MAP,
  type DistributorProductVariant,
} from 'modules/it_management/hooks/device_catalog/types'
import { useProductCatalog } from 'modules/it_management/lib/graphqlProductCatalogContext'
import {
  PRODUCT_CATALOG_CHARACTERISTIC_GROUPS_DOCUMENT,
  PRODUCT_CATALOG_VARIANTS_DOCUMENT,
} from 'modules/it_management/lib/productCatalogGraphql'

import { MarketplaceSidebar } from 'modules/it_management/components/device_catalog/MarketplaceSidebar'
import { MarketplaceProductCard } from 'modules/it_management/components/device_catalog/MarketplaceProductCard'
import { MarketplaceProductGrid } from 'modules/it_management/components/device_catalog/MarketplaceProductGrid'
import { DeviceCatalogPromoBanner } from 'modules/it_management/components/device_catalog/DeviceCatalogPromoBanner'

import emptyStateImage from './Empty-state-device-catalog-card.png'
import i18n from 'modules/core/lib/i18n'

const PER_PAGE = 12

const DeviceCatalogPage: React.FC = () => {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const { queryContext, buildProductImageUrl, loadingProductCatalogContext } = useProductCatalog()
  const { company, access } = useCurrentSession()
  const {
    isItManagementEnabled,
    loading: featureLoading,
    error: featureError,
  } = useItManagementFeatureAvailability()

  const {
    savedIds,
    loading: loadingSavedDevices,
    toggleSaved: toggleSavedDevice,
    isSaved,
  } = useDeviceCatalogSavedDevices()

  const searchFromParams = searchParams.get('search') ?? ''

  const filters = useDeviceCatalogFilters({
    externalSavedIds: savedIds,
    initialSearch: searchFromParams,
    onToggleSaved: toggleSavedDevice,
  })
  const { state, setView, setSearch, setCategory, setAttributeFilter, toggleSaved } = filters

  React.useEffect(() => {
    if (searchFromParams !== state.search) {
      setSearch(searchFromParams)
    }
  }, [searchFromParams, setSearch, state.search])

  React.useEffect(() => {
    track(trackingEvents.itManagement.deviceCatalogEvents.goToDeviceCatalog, {
      coming_from: 'side_bar',
      company_id: company.id,
      user_id: access.id,
    })
  }, [company.id, access.id])

  const [debouncedSearch, setDebouncedSearch] = React.useState('')
  React.useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(state.search), 300)
    return () => clearTimeout(timer)
  }, [state.search])

  const [page, setPage] = React.useState(1)
  React.useEffect(() => {
    setPage(1)
  }, [debouncedSearch, state.view, state.selectedCategory, state.attributeFilters])

  const categoryTypeNames = useMemo(() => {
    if (state.view !== 'category' || state.selectedCategory == null) return undefined
    return CATEGORY_TYPE_MAP[state.selectedCategory]
  }, [state.view, state.selectedCategory])

  const catalogFilter = useMemo(() => {
    const selectedAttributeFilters =
      state.view === 'category'
        ? Object.entries(state.attributeFilters)
            .filter(([, values]) => values.length > 0)
            .map(([name, values]) => ({
              name,
              values,
            }))
        : []

    if (state.view === 'saved') {
      return {
        ...(categoryTypeNames?.length ? { typeNames: categoryTypeNames } : {}),
        ...(selectedAttributeFilters.length > 0
          ? { attributeFilters: selectedAttributeFilters }
          : {}),
      }
    }

    return {
      stock: 'in_stock',
      ...(categoryTypeNames?.length ? { typeNames: categoryTypeNames } : {}),
      ...(selectedAttributeFilters.length > 0
        ? { attributeFilters: selectedAttributeFilters }
        : {}),
    }
  }, [categoryTypeNames, state.attributeFilters, state.view])

  const { data, loading } = useQuery(PRODUCT_CATALOG_VARIANTS_DOCUMENT, {
    variables: {
      search: debouncedSearch || undefined,
      page,
      perPage: PER_PAGE,
      filter: catalogFilter,
      ...(state.view === 'saved' && savedIds.length > 0 ? { ids: savedIds } : {}),
    },
    context: queryContext,
    skip: loadingProductCatalogContext,
  })

  const allItems = useMemo(
    () => data?.itManagement?.distributorProductVariantsConnection?.nodes ?? [],
    [data]
  )

  const totalCount = data?.itManagement?.distributorProductVariantsConnection?.totalCount ?? 0
  const totalPages = Math.ceil(totalCount / PER_PAGE)

  const shouldLoadCharacteristicGroups = state.view === 'category' && state.selectedCategory != null

  const { data: characteristicGroupsData } = useQuery(
    PRODUCT_CATALOG_CHARACTERISTIC_GROUPS_DOCUMENT,
    {
      variables: {
        category: state.selectedCategory ?? '',
      },
      context: queryContext,
      skip: loadingProductCatalogContext || !shouldLoadCharacteristicGroups,
    }
  )

  const dynamicFilterOptions = useMemo(
    () =>
      filters.extractFilterOptions(
        characteristicGroupsData?.itManagement?.distributorProductVariantCharacteristicGroups ?? []
      ),
    [characteristicGroupsData, filters]
  )

  const {
    sections: sidebarSections,
    activeItemId,
    searchPlaceholder,
  } = useSidebarSections({
    activeView: state.view,
    selectedCategory: state.selectedCategory,
    savedCount: savedIds.length,
    attributeFilters: state.attributeFilters,
    dynamicFilterOptions,
    setView,
    setCategory,
    setAttributeFilter,
  })

  const filteredItems = allItems

  const groups = useMemo(() => filters.groupItems(filteredItems), [filters, filteredItems])

  const goToItemDetail = (item: DistributorProductVariant) => {
    track(trackingEvents.itManagement.deviceCatalogEvents.goToDeviceDetail, {
      device_id: item.id,
      device_name: item.product?.name || item.sku || item.id,
      coming_from: 'catalog',
      company_id: company.id,
      user_id: access.id,
    })
    navigate(`/device-catalog/${item.id}`)
  }

  return (
    <ResponsivePage.Body
      variant="fullscreen"
      authorized={isItManagementEnabled}
      error={featureError}
      loading={featureLoading || loadingProductCatalogContext || loadingSavedDevices}
      height="full"
    >
      <div className="flex h-full min-h-0 w-full flex-col md:flex-row">
        <aside className="flex min-h-0 flex-shrink-0 flex-col border-r border-f1-border-secondary py-5 pl-4 pr-4 md:max-w-80 md:basis-1/4 md:pl-2 sm:pb-6">
          <div className="min-h-0 flex-1 overflow-y-auto">
            <MarketplaceSidebar sections={sidebarSections} activeItemId={activeItemId} />
          </div>
        </aside>

        <main className="flex min-h-0 flex-1 flex-col overflow-y-auto px-4 py-5 md:px-6">
          <div className="flex flex-col gap-6">
            <Input
              label={searchPlaceholder}
              hideLabel
              placeholder={searchPlaceholder}
              value={state.search}
              onChange={(value) => setSearch(String(value))}
              icon={Search}
            />

            {state.view === 'all' && <DeviceCatalogPromoBanner />}

            <MarketplaceProductGrid
              groups={groups}
              isLoading={loading}
              emptyState={{
                title: i18n.t('it_management.device_catalog.empty_state.title'),
                description: i18n.t('it_management.device_catalog.empty_state.description'),
              }}
              renderCard={(item: DistributorProductVariant) => (
                <MarketplaceProductCard
                  key={item.id}
                  {...mapVariantToCardProps(item, {
                    imageUrl: buildProductImageUrl(item.firstProductMediaImageUrl, emptyStateImage),
                    isSaved: isSaved(item.id),
                    onSaveToggle: () => toggleSaved(item.id),
                    onClick: () => goToItemDetail(item),
                  })}
                />
              )}
            />

            {totalPages > 1 && (
              <OnePagination
                totalPages={totalPages}
                currentPage={page}
                onPageChange={setPage}
                disabled={loading}
              />
            )}
          </div>
        </main>
      </div>
    </ResponsivePage.Body>
  )
}

export default DeviceCatalogPage
